import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, RefreshCw, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { useAiStatus } from '../contexts/AiStatusContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AiUsageCard — surfaces the seller's current-month AI activity (parse / expand /
 * suggest call counts + an estimated token total) on the wallet page. It is NOT
 * a billing record — the authoritative spend lives on the Emergent universal-key
 * dashboard. We show this so sellers know roughly how much AI they're using and
 * can decide whether to upgrade to Pro (which will eventually unlock higher
 * monthly AI allowances).
 *
 * Hidden when AI is globally disabled (REACT_APP_AI_ENABLED=false).
 */
export default function AiUsageCard() {
  const { language } = useLanguage();
  const { enabled } = useAiStatus();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/ai/my-usage`);
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (enabled) load();
    else setLoading(false);
  }, [enabled]);

  if (!enabled) return null;

  const isLV = language === 'lv';
  const scopeLabel = {
    parse:   isLV ? 'Meklēšanas frāzes' : 'Search phrases',
    expand:  isLV ? 'Sinonīmi'           : 'Synonym expansions',
    suggest: isLV ? 'Sludinājuma palīgs' : 'Listing assistant',
  };

  return (
    <div className="bg-white border border-[#E5E4E0] rounded-2xl p-5" data-testid="ai-usage-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#7C3AED] to-[#D97736] flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="font-medium text-[#121212]">
              {isLV ? 'Mans AI līdzeklis' : 'My AI usage'}
            </h3>
            <p className="text-xs text-[#8A8A8A]">
              {isLV ? 'Šī mēneša AI izsaukumi' : 'AI calls this month'}
            </p>
          </div>
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={load}
          disabled={loading}
          className="h-7 w-7 p-0"
          title={isLV ? 'Atjaunot' : 'Refresh'}
          data-testid="ai-usage-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {error && (
        <p className="text-xs text-red-600" data-testid="ai-usage-error">{error}</p>
      )}

      {data && !error && (
        <>
          <div className="grid grid-cols-3 gap-2 mb-3">
            {['parse', 'expand', 'suggest'].map(scope => (
              <div key={scope} className="rounded-lg bg-[#FAFAFA] border border-[#E5E4E0] p-2.5 text-center" data-testid={`ai-usage-${scope}`}>
                <div className="text-xl font-semibold text-[#121212]">
                  {data.by_scope?.[scope] ?? 0}
                </div>
                <div className="text-[10px] text-[#5C5C5C] mt-0.5 leading-tight">
                  {scopeLabel[scope]}
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between gap-2 text-xs">
            <div className="text-[#5C5C5C]">
              {isLV ? 'Kopā izsaukumi:' : 'Total calls:'}{' '}
              <span className="font-medium text-[#121212]">{data.total_calls}</span>
            </div>
            <div className="text-[#5C5C5C]" title={isLV ? 'Aptuvens tokenu skaits — precīzu summu skati Emergent profilā' : 'Estimated tokens — see Emergent profile for exact cost'}>
              ~{(data.total_tokens_est || 0).toLocaleString('lv-LV')} {isLV ? 'tokeni' : 'tokens'}
            </div>
          </div>
          <p className="text-[10px] text-[#8A8A8A] mt-2 leading-snug">
            {isLV
              ? 'Skaitļi ir aptuveni. Reālo cenu skati '
              : 'Estimates only. See real cost on '}
            <a
              href="https://app.emergent.sh/profile"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#7C3AED] hover:underline inline-flex items-center gap-0.5"
            >
              Emergent {isLV ? 'profilā' : 'profile'}
              <ExternalLink className="w-2.5 h-2.5" />
            </a>
            .
          </p>
        </>
      )}

      {loading && !data && (
        <p className="text-xs text-[#8A8A8A]">{isLV ? 'Ielādē…' : 'Loading…'}</p>
      )}
    </div>
  );
}
